import { Button, ButtonDropdown } from '@amzn/awsui-components-console';
import { useAuthState } from '../authentication/context';
import { FUSION_ASSISTANT_SETUP_GUIDES } from './constants';
import {
  buildAgentSpacesDeepLink,
  fireFusionAssistantBeacon,
} from './deepLink';
import {
  buildFusionAssistantPrompt,
  FusionAssistantContext,
} from './seedPrompt';

interface AskFusionAssistantButtonProps {
  /**
   * What the writer is looking at. Omit on list pages so the assistant starts
   * from the generic workflow prompt.
   */
  context?: FusionAssistantContext;
  /**
   * `normal` renders a split button whose dropdown offers the runtime setup
   * guides. `inline-link` renders a compact link with no dropdown, for use
   * inside table cells where a split button would dominate the row.
   */
  variant?: 'normal' | 'inline-link';
  label?: string;
  dataTestId?: string;
}

export const AskFusionAssistantButton = ({
  context,
  variant = 'normal',
  label,
  dataTestId = 'ask-fusion-assistant-button',
}: AskFusionAssistantButtonProps) => {
  const { userAlias } = useAuthState();
  const href = buildAgentSpacesDeepLink(buildFusionAssistantPrompt(context));
  const text = label ?? 'Ask Fusion Assistant';
  const onClick = () => fireFusionAssistantBeacon(userAlias);

  if (variant === 'inline-link') {
    return (
      <Button
        variant='inline-link'
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        external
        iconName='gen-ai'
        onClick={onClick}
        data-test-id={dataTestId}
      >
        {text}
      </Button>
    );
  }

  return (
    <ButtonDropdown
      expandToViewport={true}
      mainAction={{
        text,
        href,
        target: '_blank',
        rel: 'noopener noreferrer',
        external: true,
        iconName: 'gen-ai',
        onClick,
      }}
      items={FUSION_ASSISTANT_SETUP_GUIDES.map((guide) => ({
        id: guide.id,
        text: guide.text,
        href: guide.href,
        external: true,
      }))}
      data-test-id={dataTestId}
    />
  );
};
